import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Container,
  Grid,
  Card,
  CardContent,
  Button,
  Alert,
  Chip
} from '@mui/material';
import { getAuth, onAuthStateChanged, User, sendEmailVerification } from 'firebase/auth';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import SubscriptionCard from '../components/SubscriptionCard';
import TrialActivationCard from '../components/TrialActivationCard';
import PricingModal from '../components/PricingModal';

interface UserData {
  profile: {
    firstName: string;
    lastName: string;
    email: string;
    company?: string;
    phone?: string;
  };
  subscription: {
    plan: string;
    status: string;
    isTrialActivated?: boolean;
    trialStartedAt?: Date;
    trialEndsAt?: Date;
    currentPeriodEnd?: Date;
    stripePlan?: string;
    paymentMethod?: string;
    stripeSubscriptionId?: string;
    stripeCustomerId?: string;
    cancelAtPeriodEnd?: boolean;
    activatedAt?: Date;
  };
  hotelCode: string;
  ownedHotels: string[];
}

const Dashboard = () => {
  const [user, setUser] = useState<User | null>(null);
  const [userData, setUserData] = useState<UserData | null>(null);
  const [loading, setLoading] = useState(true);
  const [pricingModalOpen, setPricingModalOpen] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailVerificationSent, setEmailVerificationSent] = useState(false);

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);
      
      if (currentUser) {
        try {
          const db = getFirestore();
          const userDoc = await getDoc(doc(db, 'webUsers', currentUser.uid));
          
          if (userDoc.exists()) {
            const data = userDoc.data() as UserData;
            setUserData(data);
          }
        } catch (error) {
          console.error('Error fetching user data:', error);
        }
      }
      
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Function to refresh user data
  const fetchUserData = async (userId: string) => {
    try {
      const db = getFirestore();
      const userDoc = await getDoc(doc(db, 'webUsers', userId));

      if (userDoc.exists()) {
        const data = userDoc.data() as UserData;
        setUserData(data);
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
    }
  };

  const getDaysRemaining = () => {
    if (!userData?.subscription?.trialEndsAt) return 0;
    const trialEnd = userData.subscription.trialEndsAt instanceof Date ?
      userData.subscription.trialEndsAt :
      new Date(userData.subscription.trialEndsAt);
    const now = new Date();
    const diffTime = trialEnd.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
  };

  const handleSendEmailVerification = async () => {
    setSendingEmail(true);
    try {
      const auth = getAuth();

      if (auth.currentUser) {
        await sendEmailVerification(auth.currentUser);
        setEmailVerificationSent(true);
        alert('📧 Verification email sent! Please check your inbox and click the verification link.');
      }
    } catch (error) {
      console.error('Error sending email verification:', error);
      alert('❌ Failed to send verification email. Please try again.');
    } finally {
      setSendingEmail(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  if (!user) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <Typography>Please login to continue</Typography>
      </Box>
    );
  }

  const daysRemaining = getDaysRemaining();
  const isTrialActive = userData?.subscription?.plan === 'trial' && daysRemaining > 0;

  return (
    <Box sx={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
      py: 4
    }}>
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box mb={4}>
          <Typography variant="h4" component="h1" gutterBottom sx={{ color: '#ffffff', fontWeight: 700 }}>
            Welcome back, {userData?.profile?.firstName || user.displayName}!
          </Typography>
          <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
            Manage your hotel audio system and subscription
          </Typography>
        </Box>

        {/* Trial Status Alert */}
        {isTrialActive && (
          <Alert severity="info" sx={{ mb: 3 }}>
            Your free trial expires in {daysRemaining} days. Upgrade to continue using Sespresso.
          </Alert>
        )}

        {daysRemaining === 0 && userData?.subscription?.plan === 'trial' && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            Your free trial has expired. Please upgrade your plan to continue.
          </Alert>
        )}

        {user && !user.emailVerified && (
          <Alert
            severity="warning"
            sx={{
              mb: 3,
              background: 'rgba(74, 144, 194, 0.1)',
              border: '1px solid rgba(74, 144, 194, 0.3)',
              color: '#4A90C2',
              '& .MuiAlert-icon': {
                color: '#4A90C2'
              }
            }}
            action={
              <Button
                onClick={handleSendEmailVerification}
                disabled={sendingEmail}
                size="small"
                sx={{
                  background: 'linear-gradient(135deg, #4A90C2, #2E86AB)',
                  color: '#ffffff',
                  fontWeight: 600,
                  borderRadius: '8px',
                  px: 2,
                  '&:hover': {
                    background: 'linear-gradient(135deg, #2E86AB, #1a5f7a)',
                    transform: 'translateY(-1px)'
                  },
                  '&:disabled': {
                    background: 'rgba(74, 144, 194, 0.3)',
                    color: 'rgba(255, 255, 255, 0.6)'
                  }
                }}
              >
                {sendingEmail ? 'Sending...' : 'Verify Email'}
              </Button>
            }
          >
            Please verify your email address to make purchases and access all features.
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* Subscription Management */}
          <Grid item xs={12} lg={8}>
            {(() => {
              // Debug subscription state
              console.log('DEBUG - Subscription State:', {
                plan: userData?.subscription?.plan,
                status: userData?.subscription?.status,
                isTrialActivated: userData?.subscription?.isTrialActivated,
                shouldShowTrialCard: userData?.subscription?.plan === 'trial' &&
                                     userData?.subscription?.status === 'inactive' &&
                                     !userData?.subscription?.isTrialActivated
              });
              return null;
            })()}

            {/* Show TrialActivationCard if trial not activated */}
            {userData?.subscription?.plan === 'trial' &&
             userData?.subscription?.status === 'inactive' &&
             !userData?.subscription?.isTrialActivated && (
              <TrialActivationCard
                webUserId={user?.uid || ''}
                onTrialActivated={() => {
                  // Refresh user data after trial activation
                  if (user) {
                    fetchUserData(user.uid);
                  }
                }}
              />
            )}

            {/* Show SubscriptionCard for active subscriptions */}
            {(userData?.subscription?.isTrialActivated || userData?.subscription?.plan === 'pro') && (
              <SubscriptionCard
                subscription={userData?.subscription || null}
                onUpgradeSubscription={() => setPricingModalOpen(true)}
                onManageBilling={() => {
                  // TODO: Implement billing management
                  alert('Billing management will be implemented soon');
                }}
                onCancelSubscription={() => {
                  // TODO: Implement subscription cancellation
                  alert('Subscription cancellation will be implemented soon');
                }}
              />
            )}
          </Grid>

          {/* Account Info */}
          <Grid item xs={12} lg={4}>
            <Card sx={{
              borderRadius: '16px',
              background: 'rgba(255, 255, 255, 0.05)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              color: '#ffffff'
            }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Account Info
                </Typography>
                <Box mb={2}>
                  <Typography variant="body2" color="text.secondary">
                    Hotel Code
                  </Typography>
                  <Typography variant="h6" sx={{ fontFamily: 'monospace', letterSpacing: '0.1em' }}>
                    {userData?.hotelCode || 'N/A'}
                  </Typography>
                </Box>
                <Box mb={2}>
                  <Typography variant="body2" color="text.secondary">
                    Company
                  </Typography>
                  <Typography variant="body1">
                    {userData?.profile?.company || 'Not specified'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Email Status
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                    <Chip
                      label={user?.emailVerified ? 'Verified' : 'Unverified'}
                      size="small"
                      sx={{
                        background: user?.emailVerified
                          ? 'linear-gradient(135deg, #10b981, #059669)'
                          : 'linear-gradient(135deg, #f59e0b, #d97706)',
                        color: 'white',
                        fontWeight: 600
                      }}
                    />
                    {!user?.emailVerified && (
                      <Button
                        onClick={handleSendEmailVerification}
                        disabled={sendingEmail}
                        size="small"
                        sx={{
                          ml: 1,
                          fontSize: '0.7rem',
                          background: 'linear-gradient(135deg, #4A90C2, #2E86AB)',
                          color: '#ffffff',
                          borderRadius: '6px',
                          px: 1.5,
                          '&:hover': {
                            background: 'linear-gradient(135deg, #2E86AB, #1a5f7a)'
                          },
                          '&:disabled': {
                            background: 'rgba(74, 144, 194, 0.3)',
                            color: 'rgba(255, 255, 255, 0.6)'
                          }
                        }}
                      >
                        {sendingEmail ? 'Sending...' : 'Send Verification'}
                      </Button>
                    )}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Quick Actions */}
          <Grid item xs={12} md={6}>
            <Card sx={{
              borderRadius: '16px',
              background: 'rgba(255, 255, 255, 0.05)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              color: '#ffffff'
            }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Quick Actions
                </Typography>
                <Box display="flex" flexDirection="column" gap={2}>
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={() => window.open('https://app.rafilis.com', '_blank')}
                    sx={{
                      background: 'linear-gradient(135deg, #4A90C2, #2E86AB)',
                      borderRadius: '12px',
                      py: 1.5,
                      fontWeight: 600,
                      '&:hover': {
                        background: 'linear-gradient(135deg, #2E86AB, #1a5f7a)',
                        transform: 'translateY(-1px)'
                      }
                    }}
                  >
                    Open Hotel Control Panel
                  </Button>
                  <Button
                    variant="outlined"
                    fullWidth
                    disabled
                  >
                    View My Products
                  </Button>
                  <Button
                    variant="outlined"
                    fullWidth
                    disabled
                  >
                    Billing & Payments
                  </Button>
                  <Button
                    variant="outlined"
                    fullWidth
                    disabled
                  >
                    Account Settings
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Hotel Information */}
          <Grid item xs={12}>
            <Card sx={{
              borderRadius: '16px',
              background: 'rgba(255, 255, 255, 0.05)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              color: '#ffffff'
            }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Your Hotels
                </Typography>
                {userData?.ownedHotels && userData.ownedHotels.length > 0 ? (
                  <Grid container spacing={2}>
                    {userData.ownedHotels.map((hotelCode) => (
                      <Grid item xs={12} sm={6} md={4} key={hotelCode}>
                        <Card variant="outlined">
                          <CardContent sx={{ textAlign: 'center' }}>
                            <Typography variant="h6" sx={{ fontFamily: 'monospace' }}>
                              {hotelCode}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {userData?.profile?.company || 'Hotel'}
                            </Typography>
                            <Box mt={2}>
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => window.open('https://app.rafilis.com', '_blank')}
                                sx={{
                                  borderColor: '#4A90C2',
                                  color: '#4A90C2',
                                  borderRadius: '8px',
                                  '&:hover': {
                                    background: 'rgba(74, 144, 194, 0.1)',
                                    borderColor: '#2E86AB'
                                  }
                                }}
                              >
                                Manage
                              </Button>
                            </Box>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                ) : (
                  <Typography color="text.secondary">
                    No hotels found. Contact support if this seems incorrect.
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>

      {/* Pricing Modal */}
      <PricingModal
        open={pricingModalOpen}
        onClose={() => setPricingModalOpen(false)}
      />
    </Box>
  );
};

export default Dashboard;